import React, { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import type {
  ClassPointRewardTier,
  ClassPointSettings,
  ItemCard,
  ItemCardUserImage,
} from '../../types/pointItemNote';
import { emptyClassPointSettings } from '../../types/pointItemNote';
import { resolveItemCardImageSrc } from '../../utils/itemCardImage';
import ItemCardImageSelectModal from './ItemCardImageSelectModal';
import ItemCardStackIcon from './ItemCardStackIcon';
import UnsavedCloseConfirm from './UnsavedCloseConfirm';

type Props = {
  open: boolean;
  settings: ClassPointSettings;
  itemCardImages?: ItemCardUserImage[];
  /** 현재 선택 그룹의 학급 통합 포인트 (표시용) */
  classTotalPoints?: number;
  /** 레벨업 포인트 단위 (기본 도달 포인트 계산용) */
  levelUpPoints?: number;
  /** 현재 그룹 멤버 수 (기본 도달 포인트 계산용) */
  memberCount?: number;
  onClose: () => void;
  onSave: (settings: ClassPointSettings) => void;
  onItemCardImagesChange: (
    images: ItemCardUserImage[],
    options?: { silent?: boolean }
  ) => void;
};

const THRESHOLD_STEP = 10;

function defaultThresholdPoints(levelUpPoints: number, memberCount: number): number {
  const unit = Math.max(1, levelUpPoints || 50);
  const members = Math.max(1, memberCount || 1);
  return unit * 2 * members;
}

function newTierId(): string {
  return `cpt-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

function newCardId(): string {
  return `ic-cp-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

function cloneSettings(s: ClassPointSettings): ClassPointSettings {
  return {
    enabled: s.enabled === true,
    integrateAllGroups: s.integrateAllGroups === true,
    grantedTierByGroup: { ...s.grantedTierByGroup },
    rewardTiers: s.rewardTiers.map((t) => ({
      ...t,
      cards: t.cards.map((c) => ({ ...c })),
    })),
  };
}

function settingsEqual(a: ClassPointSettings, b: ClassPointSettings): boolean {
  return JSON.stringify(a) === JSON.stringify(b);
}

const ClassPointSettingsModal: React.FC<Props> = ({
  open,
  settings,
  itemCardImages = [],
  classTotalPoints = 0,
  levelUpPoints = 50,
  memberCount = 1,
  onClose,
  onSave,
  onItemCardImagesChange,
}) => {
  const [draft, setDraft] = useState<ClassPointSettings>(() => cloneSettings(settings));
  const [baseline, setBaseline] = useState<ClassPointSettings>(() => cloneSettings(settings));
  const [editing, setEditing] = useState<{ tierId: string; card: ItemCard } | null>(null);
  const [isNewCard, setIsNewCard] = useState(false);
  const [confirmClose, setConfirmClose] = useState(false);
  const [cardImageModalOpen, setCardImageModalOpen] = useState(false);
  const [integrateTip, setIntegrateTip] = useState<null | {
    kind: 'off' | 'on';
    left: number;
    top: number;
  }>(null);

  useEffect(() => {
    if (!open) return;
    const next = cloneSettings(settings ?? emptyClassPointSettings());
    setDraft(next);
    setBaseline(cloneSettings(next));
    setEditing(null);
    setIsNewCard(false);
    setConfirmClose(false);
    setCardImageModalOpen(false);
    setIntegrateTip(null);
  }, [open]);

  useEffect(() => {
    if (!open || cardImageModalOpen || editing != null) return;
    const next = cloneSettings(settings ?? emptyClassPointSettings());
    setDraft(next);
    setBaseline(cloneSettings(next));
    // eslint-disable-next-line react-hooks/exhaustive-deps -- editing 종료 시 부모 미반영 draft를 덮지 않음
  }, [settings, open]);

  const showIntegrateTip = (kind: 'off' | 'on', el: HTMLElement) => {
    const rect = el.getBoundingClientRect();
    const tipWidth = 260;
    const pad = 10;
    let left = rect.left + rect.width / 2 - tipWidth / 2;
    left = Math.max(pad, Math.min(left, window.innerWidth - tipWidth - pad));
    const top = rect.bottom + 8;
    setIntegrateTip({ kind, left, top });
  };

  const dirty = useMemo(() => !settingsEqual(draft, baseline), [draft, baseline]);
  const bodyActive = draft.enabled;

  const requestClose = () => {
    if (dirty || editing) {
      setConfirmClose(true);
      return;
    }
    onClose();
  };

  const addTier = () => {
    if (!bodyActive || editing) return;
    const now = new Date().toISOString();
    const last = draft.rewardTiers[draft.rewardTiers.length - 1];
    const base = defaultThresholdPoints(levelUpPoints, memberCount);
    const tier: ClassPointRewardTier = {
      id: newTierId(),
      thresholdPoints: last ? last.thresholdPoints + base : base,
      cards: [],
      createdAt: now,
      updatedAt: now,
    };
    setDraft((prev) => ({
      ...prev,
      rewardTiers: [...prev.rewardTiers, tier].sort(
        (a, b) => a.thresholdPoints - b.thresholdPoints || a.id.localeCompare(b.id)
      ),
    }));
  };

  const updateTierThreshold = (tierId: string, value: number) => {
    setDraft((prev) => ({
      ...prev,
      rewardTiers: prev.rewardTiers
        .map((t) =>
          t.id === tierId
            ? { ...t, thresholdPoints: Math.max(0, value), updatedAt: new Date().toISOString() }
            : t
        )
        .sort((a, b) => a.thresholdPoints - b.thresholdPoints || a.id.localeCompare(b.id)),
    }));
  };

  const nudgeTierThreshold = (tierId: string, delta: number) => {
    const tier = draft.rewardTiers.find((t) => t.id === tierId);
    if (!tier) return;
    updateTierThreshold(tierId, Math.max(0, tier.thresholdPoints + delta));
  };

  const removeTier = (tierId: string) => {
    if (editing?.tierId === tierId) {
      setEditing(null);
      setIsNewCard(false);
    }
    setDraft((prev) => ({
      ...prev,
      rewardTiers: prev.rewardTiers.filter((t) => t.id !== tierId),
    }));
  };

  const startAddCard = (tierId: string) => {
    if (!bodyActive || editing) return;
    const now = new Date().toISOString();
    const card: ItemCard = {
      itemCardId: newCardId(),
      cost: 'Nocharge',
      type: 'passive',
      name: '',
      memo: '',
      category: 'classPoint',
      cardImage: 'default',
      createdAt: now,
      updatedAt: now,
    };
    setDraft((prev) => ({
      ...prev,
      rewardTiers: prev.rewardTiers.map((t) =>
        t.id === tierId ? { ...t, cards: [...t.cards, card], updatedAt: now } : t
      ),
    }));
    setEditing({ tierId, card });
    setIsNewCard(true);
  };

  const startEditCard = (tierId: string, card: ItemCard) => {
    if (!bodyActive || editing) return;
    setEditing({ tierId, card: { ...card } });
    setIsNewCard(false);
  };

  const cancelEdit = () => {
    if (!editing) return;
    if (isNewCard) {
      const { tierId, card } = editing;
      setDraft((prev) => ({
        ...prev,
        rewardTiers: prev.rewardTiers.map((t) =>
          t.id === tierId
            ? { ...t, cards: t.cards.filter((c) => c.itemCardId !== card.itemCardId) }
            : t
        ),
      }));
    }
    setEditing(null);
    setIsNewCard(false);
  };

  const confirmEdit = () => {
    if (!editing || !editing.card.name.trim()) return;
    const now = new Date().toISOString();
    const nextCard: ItemCard = {
      ...editing.card,
      name: editing.card.name.trim(),
      memo: editing.card.memo.trim(),
      useCount:
        editing.card.type === 'active' ? Math.max(1, editing.card.useCount ?? 1) : undefined,
      updatedAt: now,
    };
    setDraft((prev) => ({
      ...prev,
      rewardTiers: prev.rewardTiers.map((t) =>
        t.id === editing.tierId
          ? {
              ...t,
              updatedAt: now,
              cards: t.cards.map((c) =>
                c.itemCardId === nextCard.itemCardId ? nextCard : c
              ),
            }
          : t
      ),
    }));
    setEditing(null);
    setIsNewCard(false);
  };

  const deleteCard = (tierId: string, cardId: string) => {
    setDraft((prev) => ({
      ...prev,
      rewardTiers: prev.rewardTiers.map((t) =>
        t.id === tierId
          ? { ...t, cards: t.cards.filter((c) => c.itemCardId !== cardId) }
          : t
      ),
    }));
    if (editing?.card.itemCardId === cardId) {
      setEditing(null);
      setIsNewCard(false);
    }
  };

  const handleSave = () => {
    if (editing) {
      window.alert('편집 중인 카드를 먼저 확인하거나 취소해 주세요.');
      return;
    }
    onSave(cloneSettings(draft));
    onClose();
  };

  if (!open) return null;

  return createPortal(
    <div
      className="cp-apn-modal-overlay"
      role="presentation"
      onClick={requestClose}
    >
      <div
        className="cp-apn-modal cp-pin-settings-modal cp-pin-class-point-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="cp-pin-class-point-title"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="cp-apn-modal-header cp-pin-modal-header">
          <h2 id="cp-pin-class-point-title">학급 포인트 설정</h2>
          <button
            type="button"
            className="cp-apn-icon-btn"
            aria-label="닫기"
            onClick={requestClose}
          >
            ×
          </button>
        </header>

        <div className="cp-pin-bar-tabs cp-pin-bar-tabs--2" role="tablist" aria-label="학급 포인트 사용">
          <button
            type="button"
            role="tab"
            aria-selected={!draft.enabled}
            className={`cp-pin-bar-tab${!draft.enabled ? ' is-active' : ''}`}
            onClick={() => setDraft((prev) => ({ ...prev, enabled: false }))}
          >
            OFF
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={draft.enabled}
            className={`cp-pin-bar-tab${draft.enabled ? ' is-active' : ''}`}
            onClick={() => setDraft((prev) => ({ ...prev, enabled: true }))}
          >
            ON
          </button>
        </div>

        <div
          className={`cp-apn-modal-body cp-pin-modal-body cp-pin-class-point-body${
            bodyActive ? '' : ' is-inactive'
          }`}
          aria-disabled={!bodyActive}
        >
          <section className="cp-pin-class-point-section">
            <div className="cp-pin-class-point-section-head">
              <h3 className="cp-pin-class-point-section-title">학급 포인트별 보상 기록</h3>
              <div className="cp-pin-class-point-integrate-row">
                <span className="cp-pin-class-point-integrate-label">그룹 통합 포인트</span>
                <button
                  type="button"
                  className={`team-auto-approve-toggle cp-pin-class-point-integrate-toggle${
                    draft.integrateAllGroups ? ' on' : ''
                  }`}
                  aria-pressed={draft.integrateAllGroups}
                  aria-label={
                    draft.integrateAllGroups
                      ? '그룹 통합 포인트 끄기'
                      : '그룹 통합 포인트 켜기'
                  }
                  disabled={!bodyActive}
                  onClick={() =>
                    setDraft((prev) => ({
                      ...prev,
                      integrateAllGroups: !prev.integrateAllGroups,
                    }))
                  }
                >
                  <span
                    className="cp-pin-class-point-integrate-half"
                    onMouseEnter={(e) => showIntegrateTip('off', e.currentTarget)}
                    onMouseLeave={() => setIntegrateTip(null)}
                  >
                    <span
                      className="team-auto-approve-toggle-slider"
                      data-on={!draft.integrateAllGroups}
                    >
                      OFF
                    </span>
                  </span>
                  <span
                    className="cp-pin-class-point-integrate-half"
                    onMouseEnter={(e) => showIntegrateTip('on', e.currentTarget)}
                    onMouseLeave={() => setIntegrateTip(null)}
                  >
                    <span
                      className="team-auto-approve-toggle-slider"
                      data-on={draft.integrateAllGroups}
                    >
                      ON
                    </span>
                  </span>
                </button>
              </div>
            </div>

            <ul className="cp-pin-class-point-tier-list">
              {draft.rewardTiers.map((tier) => {
                const reached =
                  tier.thresholdPoints > 0 && classTotalPoints >= tier.thresholdPoints;
                return (
                  <li key={tier.id} className="cp-pin-class-point-tier">
                    <button
                      type="button"
                      className="cp-pin-class-point-tier-del"
                      disabled={!bodyActive || !!editing}
                      onClick={() => removeTier(tier.id)}
                    >
                      구간 삭제
                    </button>
                    <div className="cp-pin-class-point-tier-layout">
                      <div className="cp-pin-class-point-reach">
                        <span className="cp-pin-class-point-reach-label">도달 포인트</span>
                        <div className="cp-pin-level-basic-control cp-pin-class-point-reach-control">
                          <button
                            type="button"
                            className="cp-pin-level-basic-nav"
                            aria-label="도달 포인트 감소"
                            disabled={!bodyActive || !!editing}
                            onClick={() => nudgeTierThreshold(tier.id, -THRESHOLD_STEP)}
                          >
                            &lt;
                          </button>
                          <input
                            className="cp-pin-level-basic-input"
                            type="text"
                            inputMode="numeric"
                            aria-label="도달 포인트"
                            disabled={!bodyActive || !!editing}
                            value={tier.thresholdPoints}
                            onChange={(e) => {
                              const v = e.target.value.trim();
                              if (v === '') updateTierThreshold(tier.id, 0);
                              else if (/^\d+$/.test(v)) {
                                updateTierThreshold(tier.id, parseInt(v, 10));
                              }
                            }}
                            onBlur={() => {
                              updateTierThreshold(
                                tier.id,
                                Math.max(0, Math.min(999999, tier.thresholdPoints || 0))
                              );
                            }}
                            onFocus={(e) => e.target.select()}
                          />
                          <button
                            type="button"
                            className="cp-pin-level-basic-nav"
                            aria-label="도달 포인트 증가"
                            disabled={!bodyActive || !!editing}
                            onClick={() => nudgeTierThreshold(tier.id, THRESHOLD_STEP)}
                          >
                            &gt;
                          </button>
                        </div>
                        <span
                          className={`cp-pin-class-point-tier-badge${
                            reached ? ' is-reached' : ''
                          }`}
                        >
                          {reached ? '달성' : '미달성'}
                        </span>
                      </div>

                      <div className="cp-pin-class-point-tier-cards">
                        <div className="cp-pin-item-grid cp-pin-class-point-card-grid">
                          {tier.cards.map((item) => {
                            const isEditing =
                              editing?.tierId === tier.id &&
                              editing.card.itemCardId === item.itemCardId;
                            return (
                              <div
                                key={item.itemCardId}
                                className={`cp-pin-item-card${
                                  isEditing ? ' is-editing' : ''
                                }`}
                                onClick={() => {
                                  if (!isEditing) startEditCard(tier.id, item);
                                }}
                                role="button"
                                tabIndex={bodyActive ? 0 : -1}
                                onKeyDown={(e) => {
                                  if (!isEditing && (e.key === 'Enter' || e.key === ' ')) {
                                    e.preventDefault();
                                    startEditCard(tier.id, item);
                                  }
                                }}
                              >
                                {isEditing && editing ? (
                                  <div
                                    className="cp-pin-item-edit-form"
                                    onClick={(e) => e.stopPropagation()}
                                  >
                                    <button
                                      type="button"
                                      className="cp-pin-item-image-btn"
                                      title="카드 이미지 선택"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setCardImageModalOpen(true);
                                      }}
                                    >
                                      {(() => {
                                        const src = resolveItemCardImageSrc(editing.card.cardImage);
                                        return src ? (
                                          <img src={src} alt="" />
                                        ) : (
                                          <span className="cp-pin-item-image-placeholder">
                                            <ItemCardStackIcon size={40} />
                                          </span>
                                        );
                                      })()}
                                    </button>
                                    <div className="cp-pin-item-type-toggle-wrap">
                                      <button
                                        type="button"
                                        className={`cp-pin-item-type-toggle${
                                          editing.card.type === 'active' ? ' is-active' : ''
                                        }`}
                                        onClick={() =>
                                          setEditing({
                                            ...editing,
                                            card: {
                                              ...editing.card,
                                              type:
                                                editing.card.type === 'active'
                                                  ? 'passive'
                                                  : 'active',
                                              useCount:
                                                editing.card.type === 'active'
                                                  ? undefined
                                                  : editing.card.useCount ?? 1,
                                            },
                                          })
                                        }
                                        aria-label="패시브/액티브 전환"
                                      >
                                        <span
                                          className={
                                            editing.card.type === 'passive' ? 'is-on' : ''
                                          }
                                        >
                                          패시브
                                        </span>
                                        <span
                                          className={
                                            editing.card.type === 'active' ? 'is-on' : ''
                                          }
                                        >
                                          액티브
                                        </span>
                                      </button>
                                    </div>
                                    <label className="cp-pin-item-field">
                                      <input
                                        type="text"
                                        value={editing.card.name}
                                        placeholder="아이템 이름"
                                        aria-label="아이템 이름"
                                        onChange={(e) =>
                                          setEditing({
                                            ...editing,
                                            card: { ...editing.card, name: e.target.value },
                                          })
                                        }
                                      />
                                    </label>
                                    {editing.card.type === 'active' ? (
                                      <label className="cp-pin-item-field">
                                        <input
                                          type="number"
                                          min={1}
                                          value={editing.card.useCount ?? 1}
                                          placeholder="사용 횟수"
                                          aria-label="사용 횟수"
                                          onChange={(e) =>
                                            setEditing({
                                              ...editing,
                                              card: {
                                                ...editing.card,
                                                useCount: Math.max(
                                                  1,
                                                  Number(e.target.value) || 1
                                                ),
                                              },
                                            })
                                          }
                                        />
                                      </label>
                                    ) : null}
                                    <label className="cp-pin-item-field cp-pin-item-field--grow">
                                      <textarea
                                        rows={2}
                                        value={editing.card.memo}
                                        placeholder="아이템 설명"
                                        aria-label="아이템 설명"
                                        onChange={(e) =>
                                          setEditing({
                                            ...editing,
                                            card: { ...editing.card, memo: e.target.value },
                                          })
                                        }
                                      />
                                    </label>
                                    <div className="cp-pin-item-card-btns">
                                      <button
                                        type="button"
                                        className="cp-pin-item-btn cp-pin-item-btn--cancel"
                                        onClick={cancelEdit}
                                      >
                                        취소
                                      </button>
                                      <button
                                        type="button"
                                        className="cp-pin-item-btn cp-pin-item-btn--ok"
                                        disabled={!editing.card.name.trim()}
                                        onClick={confirmEdit}
                                      >
                                        확인
                                      </button>
                                      <button
                                        type="button"
                                        className="cp-pin-item-btn cp-pin-item-btn--del"
                                        onClick={() =>
                                          deleteCard(tier.id, editing.card.itemCardId)
                                        }
                                      >
                                        삭제
                                      </button>
                                    </div>
                                  </div>
                                ) : (
                                  <>
                                    <div className="cp-pin-item-icon-area" aria-hidden>
                                      {(() => {
                                        const src = resolveItemCardImageSrc(item.cardImage);
                                        if (src) return <img src={src} alt="" />;
                                        return <ItemCardStackIcon size={48} />;
                                      })()}
                                    </div>
                                    <div className="cp-pin-item-name">
                                      {item.name || '이름 없음'}
                                    </div>
                                    <div className="cp-pin-item-memo">{item.memo || ''}</div>
                                  </>
                                )}
                              </div>
                            );
                          })}
                          <button
                            type="button"
                            className="cp-pin-item-add-card"
                            disabled={!bodyActive || !!editing}
                            onClick={() => startAddCard(tier.id)}
                          >
                            <span className="cp-pin-item-add-circle" aria-hidden>
                              <svg width="48" height="48" viewBox="0 0 48 48" fill="none">
                                <circle
                                  cx="24"
                                  cy="24"
                                  r="23"
                                  stroke="currentColor"
                                  strokeWidth="2"
                                  strokeDasharray="4 4"
                                  fill="none"
                                />
                                <path
                                  d="M24 14V34M14 24H34"
                                  stroke="currentColor"
                                  strokeWidth="3"
                                  strokeLinecap="round"
                                />
                              </svg>
                            </span>
                            <span>보상 카드 추가</span>
                          </button>
                        </div>
                      </div>
                    </div>
                  </li>
                );
              })}
            </ul>

            <button
              type="button"
              className="cp-pin-class-point-add-tier"
              onClick={addTier}
              disabled={!bodyActive || !!editing}
            >
              + 보상 구간 추가
            </button>
          </section>
        </div>

        <footer className="cp-pin-modal-footer">
          <button
            type="button"
            className="cp-pin-modal-btn cp-pin-modal-btn--secondary"
            onClick={requestClose}
          >
            닫기
          </button>
          <button
            type="button"
            className="cp-pin-modal-btn cp-pin-modal-btn--primary"
            onClick={handleSave}
          >
            저장
          </button>
        </footer>
      </div>

      <UnsavedCloseConfirm
        open={confirmClose}
        onCancel={() => setConfirmClose(false)}
        onConfirm={() => {
          setConfirmClose(false);
          onClose();
        }}
      />

      <ItemCardImageSelectModal
        open={cardImageModalOpen && editing != null}
        currentCardImage={editing?.card.cardImage}
        userImages={itemCardImages}
        onClose={() => setCardImageModalOpen(false)}
        onApply={(cardImage) => {
          if (!editing) return;
          setEditing({
            ...editing,
            card: { ...editing.card, cardImage },
          });
        }}
        onUserImagesChange={(images) => onItemCardImagesChange(images, { silent: true })}
      />

      {integrateTip ? (
        <div
          className="cp-pin-class-point-integrate-tip is-open"
          role="tooltip"
          style={{ left: integrateTip.left, top: integrateTip.top }}
        >
          {integrateTip.kind === 'on'
            ? '클래스 내 모든 그룹의 포인트를 합산하여 학급 포인트로 계산합니다.'
            : '클래스 내 각 그룹별로 학급 포인트를 계산합니다.'}
        </div>
      ) : null}
    </div>,
    document.body
  );
};

export default ClassPointSettingsModal;
